import { useMemo } from "react";
import { useIntlFormatterLocales } from "./useIntlFormatterLocales";

export function determineRelativeUnit(
  days: number
): Intl.RelativeTimeFormatUnit {
  if (days < 7) {
    return "days";
  } else if (days < 30) {
    return "weeks";
  } else if (days < 365) {
    return "months";
  } else {
    return "years";
  }
}

export function determineRelativeUnitAndValue(
  timestampA: number,
  timestampB: number
) {
  const diffInMilliseconds = timestampA - timestampB;
  const diffInSeconds = diffInMilliseconds / 1000;
  const diffInMinutes = diffInSeconds / 60;
  const diffInHours = diffInMinutes / 60;
  const diffInDays = diffInHours / 24;

  if (Math.abs(diffInHours) < 1) {
    return {
      value: Math.round(diffInMinutes),
      unit: "minutes" as Intl.RelativeTimeFormatUnit,
    };
  } else if (Math.abs(diffInDays) < 1) {
    return {
      value: Math.round(diffInHours),
      unit: "hours" as Intl.RelativeTimeFormatUnit,
    };
  } else if (Math.abs(diffInDays) < 7) {
    return {
      value: Math.round(diffInDays),
      unit: "days" as Intl.RelativeTimeFormatUnit,
    };
  } else if (Math.abs(diffInDays) < 30) {
    return {
      value: Math.round(diffInDays / 7),
      unit: "weeks" as Intl.RelativeTimeFormatUnit,
    };
  } else if (Math.abs(diffInDays) < 365) {
    return {
      value: Math.round(diffInDays / 30),
      unit: "months" as Intl.RelativeTimeFormatUnit,
    };
  } else {
    return {
      value: Math.round(diffInDays / 365),
      unit: "years" as Intl.RelativeTimeFormatUnit,
    };
  }
}

export function useRelativeTimeFormatter() {
  const defaultLocales = useIntlFormatterLocales();
  return useMemo(() => {
    const formatter = new Intl.RelativeTimeFormat(defaultLocales, {
      style: "short",
    });
    return (value: number, unit: Intl.RelativeTimeFormatUnit) => {
      return formatter.format(value, unit);
    };
  }, [defaultLocales]);
}
